import React, { useEffect, useState } from 'react';

import { AspectRatioWrapper, Heading } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import {
  createVirtualTourGrantApi,
  getVirtualTourViewerUrl,
  renewVirtualTourGrantApi,
} from '../../util/api';

import css from './ListingPage.module.css';
import { VIRTUAL_TOUR_SECTION_ID } from './virtualTourNavigation';
import VirtualTourPlayer from './VirtualTourPlayer';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const VIRTUAL_TOUR_RENEWAL_INTERVAL_MS = 5 * 60 * 1000;
const VIRTUAL_TOUR_RETRY_INTERVAL_MS = 60 * 1000;

const validExpiry = value => {
  const expiresAt = Date.parse(value);
  return Number.isFinite(expiresAt) && expiresAt > Date.now() ? expiresAt : null;
};

const SectionVirtualTourMaybe = props => {
  const { listingId } = props;
  const id = listingId?.uuid || listingId;
  const [viewerState, setViewerState] = useState({ status: 'checking', version: 0 });

  useEffect(() => {
    let cancelled = false;
    let requestInFlight = false;
    let grantExpiresAt = 0;
    let renewalTimer = null;
    let retryTimer = null;
    let expiryTimer = null;
    let activeController = null;
    setViewerState({ status: 'checking', version: 0 });

    if (typeof id !== 'string' || !UUID_PATTERN.test(id)) {
      setViewerState({ status: 'hidden', version: 0 });
      return undefined;
    }

    const clearTimer = timer => {
      if (timer) window.clearTimeout(timer);
    };

    const clearScheduledRequests = () => {
      clearTimer(renewalTimer);
      clearTimer(retryTimer);
      renewalTimer = null;
      retryTimer = null;
    };

    const scheduleExpiry = () => {
      clearTimer(expiryTimer);
      const remaining = grantExpiresAt - Date.now();
      if (remaining <= 0) {
        grantExpiresAt = 0;
        setViewerState(previous => ({ ...previous, status: 'unavailable' }));
        return;
      }
      expiryTimer = window.setTimeout(() => {
        grantExpiresAt = 0;
        clearScheduledRequests();
        if (!cancelled) {
          setViewerState(previous => ({ ...previous, status: 'unavailable' }));
        }
      }, remaining);
    };

    let requestGrant;
    let renewGrant;

    const scheduleRenewal = () => {
      clearTimer(renewalTimer);
      renewalTimer = null;
      if (document.visibilityState !== 'visible' || !grantExpiresAt) return;
      renewalTimer = window.setTimeout(() => renewGrant(), VIRTUAL_TOUR_RENEWAL_INTERVAL_MS);
    };

    const scheduleRetry = callback => {
      clearTimer(retryTimer);
      retryTimer = null;
      if (document.visibilityState !== 'visible') return;
      const remaining = grantExpiresAt ? grantExpiresAt - Date.now() : Infinity;
      if (remaining <= 0) {
        grantExpiresAt = 0;
        setViewerState(previous => ({ ...previous, status: 'unavailable' }));
        return;
      }
      retryTimer = window.setTimeout(callback, Math.min(VIRTUAL_TOUR_RETRY_INTERVAL_MS, remaining));
    };

    requestGrant = async () => {
      if (cancelled || requestInFlight || document.visibilityState !== 'visible') return;
      requestInFlight = true;
      activeController = new AbortController();

      try {
        const response = await createVirtualTourGrantApi(id, {
          signal: activeController.signal,
          cache: 'no-store',
        });
        if (cancelled) return;

        const expiresAt = validExpiry(response?.expiresAt);
        if (response?.hasVirtualTour !== true || !expiresAt) throw new Error('Invalid grant');

        grantExpiresAt = expiresAt;
        setViewerState(previous => ({ status: 'ready', version: previous.version + 1 }));
        scheduleExpiry();
        scheduleRenewal();
      } catch (error) {
        if (cancelled || error?.name === 'AbortError') return;
        grantExpiresAt = 0;
        clearTimer(expiryTimer);
        expiryTimer = null;

        if (error?.status === 404) {
          setViewerState(previous => ({ ...previous, status: 'hidden' }));
        } else {
          setViewerState(previous => ({ ...previous, status: 'unavailable' }));
          if (error?.status !== 403) scheduleRetry(requestGrant);
        }
      } finally {
        requestInFlight = false;
        activeController = null;
      }
    };

    renewGrant = async () => {
      if (cancelled || requestInFlight || document.visibilityState !== 'visible') return;
      if (!grantExpiresAt || grantExpiresAt <= Date.now()) {
        grantExpiresAt = 0;
        return requestGrant();
      }

      requestInFlight = true;
      activeController = new AbortController();
      try {
        const response = await renewVirtualTourGrantApi(id, {
          signal: activeController.signal,
          cache: 'no-store',
        });
        if (cancelled) return;

        const expiresAt = validExpiry(response?.expiresAt);
        if (response?.hasVirtualTour !== true || !expiresAt) throw new Error('Invalid renewal');
        grantExpiresAt = expiresAt;
        scheduleExpiry();
        scheduleRenewal();
      } catch (error) {
        if (cancelled || error?.name === 'AbortError') return;
        if (error?.status === 403) {
          grantExpiresAt = 0;
          clearTimer(expiryTimer);
          setViewerState(previous => ({ ...previous, status: 'unavailable' }));
          retryTimer = window.setTimeout(requestGrant, 0);
        } else {
          scheduleRetry(renewGrant);
        }
      } finally {
        requestInFlight = false;
        activeController = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        clearScheduledRequests();
        activeController?.abort();
        return;
      }

      if (grantExpiresAt > Date.now()) {
        renewGrant();
      } else {
        requestGrant();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    requestGrant();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearScheduledRequests();
      clearTimer(expiryTimer);
      activeController?.abort();
    };
  }, [id]);

  if (viewerState.status === 'hidden') return null;

  if (viewerState.status === 'checking') {
    return (
      <span id={VIRTUAL_TOUR_SECTION_ID} className={css.virtualTourAnchor} aria-hidden="true" />
    );
  }

  const isReady = viewerState.status === 'ready';

  return (
    <section
      id={VIRTUAL_TOUR_SECTION_ID}
      className={css.sectionVirtualTour}
      aria-label="360° virtual tour"
    >
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.virtualTourTitle" />
      </Heading>
      <p className={`${css.ingress} ${css.virtualTourDescription}`}>
        <FormattedMessage id="ListingPage.virtualTourDescription" />
      </p>
      <AspectRatioWrapper className={css.virtualTourFrame} width={16} height={9}>
        {isReady ? (
          <VirtualTourPlayer key={viewerState.version} src={getVirtualTourViewerUrl(id)} />
        ) : (
          <div className={css.virtualTourStatus} role="status">
            <FormattedMessage id="ListingPage.virtualTourUnavailable" />
          </div>
        )}
      </AspectRatioWrapper>
    </section>
  );
};

export default SectionVirtualTourMaybe;
